/**
 * Amount math for the grams control: the − and + steps, the limits, reading a
 * typed amount, how a size reads on its chip ("1 bar (55 g)" and its calories),
 * and the amounts last logged ("Last time").
 */
import type { LoggedAmount } from '../../shared/api';
import type { Serving } from '../../shared/foods';

/** − and + move by this much at every amount (grams, or ml for drinks). */
export const GRAMS_STEP = 5;
/** − doesn't go below this. */
export const MIN_GRAMS = 1;
/** The largest amount anything accepts (the API's limit too). */
export const MAX_GRAMS = 5000;
/** Amounts are kept to 0.1 g, so this is the least that can be logged. */
const LEAST_GRAMS = 0.1;

/** Amounts are kept to 0.1 g. */
export const tenth = (g: number) => Math.round(g * 10) / 10;

export const clampGrams = (g: number) => Math.min(MAX_GRAMS, Math.max(MIN_GRAMS, tenth(g)));

/** +: the next multiple of 5 above the amount (55 → 60, 57 → 60), up to the cap. */
export function stepUp(grams: number): number {
  const g = tenth(grams);
  return Math.max(g, clampGrams((Math.floor(g / GRAMS_STEP) + 1) * GRAMS_STEP));
}

/** −: the next multiple of 5 below the amount (55 → 50, 57 → 55), down to 1 g. */
export function stepDown(grams: number): number {
  // At or under 1 g (a pinch of something), − leaves the amount as it is.
  if (grams <= MIN_GRAMS) return grams;
  return clampGrams((Math.ceil(tenth(grams) / GRAMS_STEP) - 1) * GRAMS_STEP);
}

/** A typed amount ("57,5" works too), kept between 0.1 g and the cap. Null when it isn't a positive number. */
export function parseAmount(raw: string): number | null {
  const n = Number(raw.replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? Math.min(MAX_GRAMS, Math.max(LEAST_GRAMS, tenth(n))) : null;
}

/** A weight stated in the label itself: "1 bar (55 g)", "100 g", "1 cup (240 ml)". */
const STATES_WEIGHT = /\b\d+(\.\d+)?\s?(g|ml)\b/i;

/** How a size reads on its chip: its label, with the weight added when the label doesn't say it ("1 large egg (50 g)"). */
export function sizeLabel(serving: Serving, liquid = false): string {
  return STATES_WEIGHT.test(serving.label) ? serving.label : `${serving.label} (${tenth(serving.grams)} ${liquid ? 'ml' : 'g'})`;
}

/** Calories in one size, from the food's calories per 100 g. Null when the food has none. */
export function sizeKcal(grams: number, kcalPer100: number | null | undefined): number | null {
  return typeof kcalPer100 === 'number' && Number.isFinite(kcalPer100) ? Math.round((kcalPer100 * grams) / 100) : null;
}

/** The amount last logged of a food, and how recently: rank 0 is the food logged most recently. */
export interface LastAmount {
  grams: number;
  rank: number;
}

/** The API's list of amounts last logged (most recent first), by ref, each with its place in that order. */
export function toLastAmounts(amounts: readonly LoggedAmount[]): ReadonlyMap<string, LastAmount> {
  return new Map(amounts.map(({ ref, grams }, rank) => [ref, { grams, rank }]));
}
